'use client';

import type { fixedSizing } from '@/tailwind/sizing';
import type { fixedSpacing } from '@/tailwind/spacing';
import { useWindowSize } from '@react-hookz/web';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../tailwind.config';

type ThemeConfig = typeof tailwindConfig.theme;

const config = resolveConfig(tailwindConfig);

export type Breakpoint = keyof ThemeConfig['screens'];
export type Spacing = keyof typeof fixedSpacing;
export type Sizing = keyof typeof fixedSizing | Spacing;

const screens = Object.entries(config.theme.screens)
  .reverse()
  .map(([breakpoint, width]) => [breakpoint, +width.slice(0, -2)]) as [
  Breakpoint,
  number,
][];

const screensObject = Object.fromEntries(screens) as {
  [BP in Breakpoint]: number;
};

export const useWidth = () => useWindowSize(undefined, true).width || 600;

export const useBreakpoint = (): Breakpoint => {
  const width = useWidth();

  for (const [breakpoint, breakpointMinWidth] of screens) {
    if (width >= breakpointMinWidth) {
      return breakpoint;
    }
  }

  return 'xxs';
};

export const useBreakpointUp = (breakpoint: Breakpoint) =>
  useWidth() >= screensObject[breakpoint];

export const useBreakpointDown = (breakpoint: Breakpoint) =>
  useWidth() < screensObject[breakpoint];

export const useBreakpointBetween = (
  lowerBreakpoint: Breakpoint,
  upperBreakpoint: Breakpoint,
) => {
  const width = useWidth();

  return (
    width >= screensObject[lowerBreakpoint] &&
    width < screensObject[upperBreakpoint]
  );
};

const pxValue = (value: string) => {
  if (value.endsWith('px')) return +value.slice(0, -2);

  if (value.startsWith('var')) {
    return +getComputedStyle(document.body)
      .getPropertyValue(value.slice(4, -1))
      .slice(0, -2);
  }

  return 0;
};

export const useSpace = (spacing: Spacing) =>
  pxValue(config.theme.space[spacing]);

export const useSize = (sizing: Sizing) => pxValue(config.theme.size[sizing]);
